/**
 * CommandAutocomplete — slash-command dropdown, sibling of `MentionAutocomplete`.
 *
 * Shown when the user types `/` in a composer textarea (at the start, or
 * after whitespace once a letter follows). Each row is the command name plus
 * its short label, the Claude Code shape. The prose preview of the attached
 * skill body is OPT-IN (`showPreview`): by default nothing explains itself
 * until the reader picks, and no skill body is fetched.
 *
 * Keyboard: up/down to move the highlight, Enter or Tab to pick, Escape to
 * dismiss. Options come from `useThreadCommands`. When a row carries a
 * template `token`, the preview/snippet load the attached skill body
 * (house guidance), never the step prompt (JSON / SurveyJS contracts).
 *
 * Unlike `MentionAutocomplete`, this component is self-contained: it listens
 * on `document` for arrow/Enter/Escape while it has rows to show, so it can
 * be driven from a textarea it does not own.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { SquareSlash } from 'lucide-react'

import {
  ComposerAppearance,
  composerPanelClassName,
} from '@/components/composer/composerAppearance'
import { Text } from '@/components/ui/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useCommandDrivingCopies } from '@/lib/workflows/commandDrivingCopy'
import { cn } from '@/lib/utils'

export interface CommandOption {
  command: string
  label: string
  /** Full copy shown in the hover preview; the row shows the start of this. */
  description?: string
  /** Template token — when set, preview prefers the attached skill body. */
  token?: string
}

/** Hover preview is prompt text only, capped so a long blurb cannot grow the card. */
export const COMMAND_PREVIEW_CHAR_LIMIT = 160

export function truncateCommandPreview(
  text: string,
  limit: number = COMMAND_PREVIEW_CHAR_LIMIT,
): string {
  const trimmed = text.trim()
  if (trimmed.length <= limit) {
    return trimmed
  }

  return `${trimmed.slice(0, limit).trimEnd()}…`
}

export type CommandPreviewPlacement = 'side' | 'above'

interface CommandAutocompleteProps {
  options: Array<CommandOption>
  query: string
  onSelect: (option: CommandOption) => void
  onDismiss: () => void
  appearance?: ComposerAppearance
  /**
   * `side` is the home composer (room to the right). `above` is the thread
   * column, where a side card is clipped by the pane.
   */
  previewPlacement?: CommandPreviewPlacement
  /**
   * Show the highlighted row's prose (attached skill body, else description)
   * beside or above the list. Off by default: the row's label is the whole
   * explanation, and the skill bodies are not fetched.
   */
  showPreview?: boolean
}

/** Strips the leading '/' so `/report` and `report` compare the same way. */
function commandName(command: string): string {
  return command.startsWith('/') ? command.slice(1) : command
}

function optionCopy(option: CommandOption): string {
  const description = option.description?.trim()
  return description && description.length > 0 ? description : option.label
}

/* Name and label only: matching the prose too made `/tes` list every command
   whose copy contained "tes" (`Writes…`), which is noise, not a match. */
function optionHaystack(option: CommandOption): string {
  return [commandName(option.command), option.label].join(' ').toLowerCase()
}

export function highlightCommandQuery(text: string, query: string): ReactNode {
  if (!query) {
    return text
  }

  const index = text.toLowerCase().indexOf(query.toLowerCase())
  if (index === -1) {
    return text
  }

  return (
    <>
      {text.slice(0, index)}
      <span className="text-primary">{text.slice(index, index + query.length)}</span>
      {text.slice(index + query.length)}
    </>
  )
}

/**
 * The rows this component would show for `query`. Exported so a caller can ask
 * "is the dropdown live right now?" WITHOUT duplicating the match rule.
 */
export function filterCommandOptions(
  options: Array<CommandOption>,
  query: string,
): Array<CommandOption> {
  const needle = query.toLowerCase()
  if (!needle) {
    return options
  }

  return options.filter((option) => optionHaystack(option).includes(needle))
}

export function CommandAutocomplete(props: CommandAutocompleteProps) {
  const {
    options,
    query,
    onSelect,
    onDismiss,
    appearance = 'external',
    previewPlacement = 'side',
    showPreview = false,
  } = props
  const [activeIndex, setActiveIndex] = useState(0)

  /* Skill bodies are fetched for every row only when the preview card is on.
     Otherwise only the highlighted row's body is fetched, lazily, for its
     hover tooltip: the filter no longer reads the prose, so the rows can be
     decided without it. */
  const rawFiltered = filterCommandOptions(options, query)
  const rawActive = rawFiltered[activeIndex] ?? rawFiltered[0] ?? null
  const tokens = useMemo(
    () =>
      showPreview
        ? options
            .map((option) => option.token)
            .filter(
              (token): token is string =>
                typeof token === 'string' && token.length > 0,
            )
        : rawActive?.token
          ? [rawActive.token]
          : [],
    [options, showPreview, rawActive?.token],
  )
  const drivingByToken = useCommandDrivingCopies(tokens)
  const resolvedOptions = useMemo(
    () =>
      options.map((option) => {
        const driving = option.token
          ? drivingByToken.get(option.token)
          : undefined
        if (!driving) {
          return option
        }
        return { ...option, description: driving }
      }),
    [options, drivingByToken],
  )

  const filtered = filterCommandOptions(resolvedOptions, query)
  const active = filtered[activeIndex] ?? filtered[0] ?? null

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  /* The listener still has to live on `document` (this component does not own
     the textarea it reacts to — see the doc comment above), but it must not
     act on ANY textarea on the page, only the one belonging to the composer
     that rendered THIS dropdown. Two composers can be mounted at once (e.g. a
     thread composer plus a run-gate composer); without this check, whichever
     dropdown is open swallows the other composer's Enter/Tab/Escape too.
     `containerRef` sits on this dropdown's own root node, so its parent is
     the composer's shared wrapper — the same ancestor the textarea renders
     under as a sibling. */
  const containerRef = useRef<HTMLDivElement>(null)

  /* Latest-value refs so the listener can be subscribed exactly once instead
     of tearing down and re-adding a `document` listener on every keystroke
     (previously: deps `[filtered, activeIndex, onSelect, onDismiss]`, all of
     which change on effectively every render). */
  const filteredRef = useRef(filtered)
  filteredRef.current = filtered
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const rows = filteredRef.current
      if (rows.length === 0) {
        return
      }

      const focused = document.activeElement
      if (!(focused instanceof HTMLTextAreaElement)) {
        return
      }
      if (!containerRef.current?.parentElement?.contains(focused)) {
        return
      }

      if (event.key === 'Enter' && event.shiftKey) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, rows.length - 1))
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
      } else if (event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey)) {
        event.preventDefault()
        const selected = rows[activeIndexRef.current] ?? rows[0]
        if (selected) {
          onSelectRef.current(selected)
        }
      } else if (event.key === 'Escape') {
        event.preventDefault()
        onDismissRef.current()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  if (filtered.length === 0) {
    return null
  }

  const panel = composerPanelClassName(appearance)
  const previewCopy =
    showPreview && active ? truncateCommandPreview(optionCopy(active)) : ''
  const preview =
    previewCopy ? (
      <div
        className={cn(
          'rounded-xl px-3 py-2.5',
          previewPlacement === 'above'
            ? 'mb-1.5 w-full'
            : 'absolute left-full top-0 ml-2 w-[240px] max-w-[calc(100vw-3rem)]',
          panel,
        )}
        data-testid="command-autocomplete-preview"
      >
        <Text
          as="p"
          size="xs"
          color="muted"
          className="leading-5"
          data-testid="command-autocomplete-preview-body"
        >
          {previewCopy}
        </Text>
      </div>
    ) : null

  return (
    <div
      ref={containerRef}
      className="absolute bottom-full left-0 z-50 mb-2"
      data-testid="command-autocomplete"
    >
      <div
        className={cn(
          'relative flex',
          previewPlacement === 'above'
            ? 'w-[320px] max-w-[calc(100vw-3rem)] flex-col'
            : 'items-start',
        )}
      >
        {previewPlacement === 'above' ? preview : null}
        <div
          className={cn(
            'w-[320px] max-w-[calc(100vw-3rem)] max-h-64 overflow-y-auto rounded-xl p-1',
            panel,
          )}
        >
          {filtered.map((option, i) => {
            const name = commandName(option.command)
            const snippet = option.label
            const isActive = i === activeIndex
            /* The prose lives in a hover tooltip, not on the row: the label
               names the command, the tooltip explains it on demand. */
            const copy = truncateCommandPreview(optionCopy(option))
            const tooltipCopy = copy && copy !== option.label ? copy : ''

            const row = (
              <button
                key={option.command}
                type="button"
                className={cn(
                  'flex w-full items-baseline gap-2 rounded-lg px-2.5 py-2 text-left transition-colors',
                  isActive
                    ? appearance === 'internal'
                      ? 'bg-[var(--home-pill-track-bg)]'
                      : 'bg-muted/70'
                    : appearance === 'internal'
                      ? 'hover:bg-[var(--home-pill-track-bg)]'
                      : 'hover:bg-muted/70',
                )}
                onMouseDown={(event) => {
                  event.preventDefault()
                  onSelect(option)
                }}
                onMouseEnter={() => setActiveIndex(i)}
                data-testid={`command-autocomplete-option-${name}`}
              >
                <SquareSlash
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <span
                  className={cn(
                    'shrink-0 font-medium',
                    appearance === 'internal'
                      ? 'text-[13px] text-[var(--home-composer-fg)]'
                      : 'text-xs text-foreground',
                  )}
                >
                  {highlightCommandQuery(option.command, query)}
                </span>
                <span
                  className={cn(
                    'min-w-0 truncate',
                    appearance === 'internal'
                      ? 'text-[12px] text-[var(--home-pill-inactive-fg)]'
                      : 'text-xs text-muted-foreground',
                  )}
                  data-testid={`command-autocomplete-snippet-${name}`}
                >
                  {snippet}
                </span>
              </button>
            )

            if (!tooltipCopy) {
              return row
            }
            return (
              <TooltipProvider key={option.command} delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>{row}</TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="max-w-[260px] text-[12px] leading-snug"
                    data-testid={`command-autocomplete-tooltip-${name}`}
                  >
                    {tooltipCopy}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })}
        </div>

        {previewPlacement === 'side' ? preview : null}
      </div>
    </div>
  )
}
